import { z } from 'zod';
import { SportNameSchema } from './profile.js';
import type { SportName } from './profile.js';

/**
 * F12 sub-venue types + Zod schemas.
 *
 * Decisions: see plans/12_F12_venue_profile_self_service.md (Decision 2).
 *
 * ─── Status lifecycle ────────────────────────────────────────────────────────
 *   active → retired (soft-delete; bookings keep their FK reference)
 *
 * Every claimed venue has ≥1 sub-venue row from F12 onward (auto-seeded by the
 * API on first editor open). Multi-court venues get one row per court; single-
 * court venues get one default row whose name is localised to the venue's
 * primary sport (e.g. "Court 1" / "Pitch 1" / "Pool 1").
 */

// ─── Enum constants ──────────────────────────────────────────────────────────

export const SUB_VENUE_STATUSES = ['active', 'retired'] as const;
export type SubVenueStatus = (typeof SUB_VENUE_STATUSES)[number];

// ─── Raw DB row shape ────────────────────────────────────────────────────────

export type SubVenue = {
  readonly id: string;
  readonly venueId: string;
  readonly name: string;
  readonly ordinal: number;
  readonly sportName: SportName | null;
  readonly capacity: number | null;
  readonly status: SubVenueStatus;
  readonly countryCode: string;
  readonly createdAt: string;
  readonly updatedAt: string;
};

// ─── Zod schemas ─────────────────────────────────────────────────────────────

export const SubVenueStatusSchema = z.enum(SUB_VENUE_STATUSES);

/**
 * Create-sub-venue payload. `ordinal` is server-assigned (next available
 * within the venue) — the editor never sends it. `name` is required (1–80
 * chars; the SQL CHECK is permissive — app-layer Zod enforces the bound).
 */
export const CreateSubVenueInputSchema = z
  .object({
    name: z.string().min(1).max(80),
    sportName: SportNameSchema.optional(),
    capacity: z.number().int().positive().optional(),
  })
  .strict();
export type CreateSubVenueInput = z.infer<typeof CreateSubVenueInputSchema>;

/**
 * Partial update. `ordinal` is mutated via a dedicated reorder endpoint so it
 * stays unique within the venue; the editor never patches it directly.
 * `status` toggles 'active' ↔ 'retired'; the service refuses delete if any
 * bookings reference the sub-venue (422 SUB_VENUE_HAS_BOOKINGS) — owners
 * retire instead.
 */
export const UpdateSubVenueInputSchema = z
  .object({
    name: z.string().min(1).max(80).optional(),
    sportName: SportNameSchema.nullable().optional(),
    capacity: z.number().int().positive().nullable().optional(),
    status: SubVenueStatusSchema.optional(),
  })
  .strict();
export type UpdateSubVenueInput = z.infer<typeof UpdateSubVenueInputSchema>;
